import { Loader2, User, ListTree, KeyRound } from 'lucide-react'
import { Badge, BadgeVariant } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import type { IdentitySnapshot } from '@/types/snapshot'
import { AttributeValueType } from '@/types/attribute'
import { IdentityLifecycleStateBadge } from '@/types/identity'
import { useI18n } from '@/i18n'
import {EmptyState} from '@/components/shared/EmptyState'
import { formatDate } from '@/lib/date-utils'

interface IdentitySnapshotViewProps {
    data: IdentitySnapshot | undefined
    isLoading: boolean
    error: Error | null
}

function formatAttrValue(value: unknown, valueType: AttributeValueType): string {
    return AttributeValueType.format(value, valueType)
}

export function IdentitySnapshotView({ data, isLoading, error }: IdentitySnapshotViewProps) {
    const i18n = useI18n()

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">{i18n.identitySnapshot.loadingIdentityState}</span>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-8 text-destructive">
                <p>{i18n.identitySnapshot.failedToLoad}</p>
                <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
            </div>
        )
    }

    if (!data) {
        return (
            <EmptyState
                icon={User}
                title={String(i18n.identitySnapshot.noIdentityState)}
            />
        )
    }

    const { snapshot } = data

    return (
        <div className="space-y-6">
            {/* Identity Details */}
            <div className="border rounded-lg">
                <div className="flex items-center gap-2 p-3 bg-muted/50 border-b">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{i18n.identitySnapshot.identityState}</span>
                </div>
                <div className="p-4">
                    <dl className="grid grid-cols-2 gap-4">
                        <div>
                            <dt className="text-sm text-muted-foreground">{i18n.identitySnapshot.identityType}</dt>
                            <dd className="font-medium">{snapshot.identityType}</dd>
                        </div>
                        <div>
                            <dt className="text-sm text-muted-foreground">{i18n.identitySnapshot.lifecycleState}</dt>
                            <dd>
                                {(() => {
                                    const lifecycleBadge = IdentityLifecycleStateBadge.of(snapshot.lifecycleState)
                                    const StateIcon = lifecycleBadge?.icon
                                    return (
                                        <Badge variant={lifecycleBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1">
                                            {StateIcon && <StateIcon className="h-3 w-3" aria-hidden="true"/>}
                                            {snapshot.lifecycleState}
                                        </Badge>
                                    )
                                })()}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-muted-foreground">{i18n.identitySnapshot.party}</dt>
                            <dd className="font-medium">{snapshot.party.displayName}</dd>
                        </div>
                        <div>
                            <dt className="text-sm text-muted-foreground">{i18n.common.createdAt}</dt>
                            <dd className="font-medium">{formatDate(snapshot.createdAt)}</dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Identifiers */}
            {snapshot.identifiers.length > 0 && (
                <div className="border rounded-lg">
                    <div className="flex items-center gap-2 p-3 bg-muted/50 border-b">
                        <KeyRound className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{i18n.identitySnapshot.identifiers}</span>
                        <Badge variant="outline" className="ml-auto">
                            {snapshot.identifiers.length}
                        </Badge>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-identifier-type">{i18n.common.type}</TableHead>
                                <TableHead data-testid="col-identifier-value">{i18n.common.value}</TableHead>
                                <TableHead data-testid="col-identifier-primary">{i18n.common.primary}</TableHead>
                                <TableHead data-testid="col-identifier-valid-from">{i18n.common.validFrom}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {snapshot.identifiers.map((identifier) => (
                                <TableRow key={identifier.id}>
                                    <TableCell className="font-medium">
                                        {identifier.identifierType}
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">
                                        {identifier.identifierValue}
                                    </TableCell>
                                    <TableCell>
                                        {identifier.primary && (
                                            <Badge variant={BadgeVariant.SUCCESS}>{i18n.common.primary}</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatDate(identifier.validFrom)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Attributes */}
            {snapshot.attributes.length > 0 && (
                <div className="border rounded-lg">
                    <div className="flex items-center gap-2 p-3 bg-muted/50 border-b">
                        <ListTree className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{i18n.identitySnapshot.attributes}</span>
                        <Badge variant="outline" className="ml-auto">
                            {snapshot.attributes.length}
                        </Badge>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-attribute-name">{i18n.common.name}</TableHead>
                                <TableHead data-testid="col-attribute-value">{i18n.common.value}</TableHead>
                                <TableHead data-testid="col-attribute-type">{i18n.common.type}</TableHead>
                                <TableHead data-testid="col-attribute-observed">{i18n.common.observed}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {snapshot.attributes.map((attr) => (
                                <TableRow key={attr.id}>
                                    <TableCell className="font-medium">{attr.displayName || attr.name}</TableCell>
                                    <TableCell className="max-w-xs truncate">
                                        {formatAttrValue(attr.value, attr.valueType)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="text-xs">
                                            {attr.valueType}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatDate(attr.observedAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {snapshot.identifiers.length === 0 && snapshot.attributes.length === 0 && (
                <div className="text-center text-muted-foreground py-4">
                    {i18n.identitySnapshot.noIdentifiersOrAttributes}
                </div>
            )}
        </div>
    )
}
